import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { Team } from '@/types';
import { logTeamActivity } from '@/utils/activityLogger';
import { useTenant } from '@/contexts/TenantContext';

export const useTeams = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);
  const { toast } = useToast();
  const { currentTenant } = useTenant();

  const fetchTeams = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isLoading) {
      return;
    }
    
    // Rate limiting: don't fetch if we just fetched within the last 1 second
    const now = Date.now();
    if (now - lastFetch < 1000) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setLastFetch(now);
      
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) {
        console.error('Error getting tenant ID:', tenantError);
        throw new Error('Failed to get tenant ID');
      }
      
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select(`
          id,
          name,
          type,
          manager_id,
          biz_dev_id,
          created_at,
          updated_at,
          empower_created,
          ses_created
        `)
        .eq('tenant_id', tenantId)
        .order('name', { ascending: true });

      if (teamError) {
        throw teamError;
      }

      if (!teamData || teamData.length === 0) {
        setTeams([]);
        return [];
      }

      const userIds = new Set<string>();
      teamData.forEach(team => {
        if (team.manager_id) userIds.add(team.manager_id);
        if (team.biz_dev_id) userIds.add(team.biz_dev_id);
      });

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, first_name, last_name')
        .in('id', Array.from(userIds));

      if (userError) {
        throw userError;
      }

      const userMap = new Map<string, { first_name: string; last_name: string }>();
      userData.forEach(user => {
        userMap.set(user.id, { first_name: user.first_name, last_name: user.last_name });
      });

      const transformedData = teamData.map(team => ({
        ...team,
        manager_name: team.manager_id && userMap.get(team.manager_id)
          ? `${userMap.get(team.manager_id)!.first_name} ${userMap.get(team.manager_id)!.last_name}`
          : 'N/A',
        biz_dev_rep_name: team.biz_dev_id && userMap.get(team.biz_dev_id)
          ? `${userMap.get(team.biz_dev_id)!.first_name} ${userMap.get(team.biz_dev_id)!.last_name}`
          : 'N/A',
        empower_created: team.empower_created || false,
        ses_created: team.ses_created || false
      }));
      
      setTeams(transformedData);
      return transformedData;
    } catch (err: any) {
      setError('Failed to load teams: ' + (err.message || 'Unknown error'));
      toast({
        title: 'Error',
        description: 'Failed to load teams: ' + (err.message || 'Unknown error'),
        variant: 'destructive',
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [toast, currentTenant?.tenant_id]);

  const createTeam = useCallback(async (teamData: Omit<Team, 'id'>) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to perform this action.',
          variant: 'destructive',
        });
        throw new Error('No active session');
      }

      const { name, type, manager_id, biz_dev_id } = teamData;
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('teams')
        .insert({
          name,
          type,
          manager_id: manager_id || null,
          biz_dev_id: biz_dev_id || null,
          tenant_id: tenantId,
          empower_created: false,
          ses_created: false
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to create team');
      }

      await fetchTeams();
      
      // Log the team creation activity
      await logTeamActivity.created(data.id, {
        name: data.name,
        type: data.type,
        manager_id: data.manager_id,
        biz_dev_id: data.biz_dev_id
      });
      
      toast({
        title: 'Success',
        description: 'Team created successfully',
      });
      
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to create team');
      toast({
        title: 'Error',
        description: err.message || 'Failed to create team',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [toast, fetchTeams]);

  const updateTeam = useCallback(async (teamData: Team) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to perform this action.',
          variant: 'destructive',
        });
        throw new Error('No active session');
      }

      const { id, name, type, manager_id, biz_dev_id, empower_created, ses_created } = teamData;

      const { data, error } = await supabase
        .from('teams')
        .update({
          name,
          type,
          manager_id: manager_id || null,
          biz_dev_id: biz_dev_id || null,
          empower_created,
          ses_created
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to update team');
      }

      await fetchTeams();
      
      // Log the team update activity
      await logTeamActivity.updated(data.id, {
        name: data.name,
        type: data.type,
        manager_id: data.manager_id,
        biz_dev_id: data.biz_dev_id
      });
      
      toast({
        title: 'Success',
        description: 'Team updated successfully',
      });
      
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to update team');
      toast({
        title: 'Error',
        description: err.message || 'Failed to update team',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [toast, fetchTeams]);

  const deleteTeam = useCallback(async (teamId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to perform this action.',
          variant: 'destructive',
        });
        throw new Error('No active session');
      }

      const functionsBaseUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || '/functions/v1';
      const response = await fetch(`${functionsBaseUrl}/delete-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({ teamId })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete team');
      }

      await fetchTeams();
      
      // Log the team deletion activity
      await logTeamActivity.deleted(teamId, {
        deleted_team_id: teamId
      });
      
      toast({
        title: 'Success',
        description: 'Team deleted successfully',
      });
      
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to delete team. Make sure no users are assigned to this team.');
      toast({
        title: 'Error',
        description: err.message || 'Failed to delete team. Make sure no users are assigned to this team.',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [toast, fetchTeams]);

  const createTeamWithEmpower = useCallback(async (teamId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to perform this action.',
          variant: 'destructive',
        });
        throw new Error('No active session');
      }

      // Check if team has already been created with Empower
      const { data: teamCheck, error: teamCheckError } = await supabase
        .from('teams')
        .select('empower_created')
        .eq('id', teamId)
        .single();

      if (teamCheckError) {
        throw new Error(teamCheckError.message || 'Failed to check team status');
      }

      if (teamCheck.empower_created) {
        toast({
          title: 'Warning',
          description: 'This team has already been created with Empower.',
          variant: 'default',
        });
        return;
      }

      // Fetch team name
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('name')
        .eq('id', teamId)
        .single();

      if (teamError || !teamData) {
        throw new Error(teamError?.message || 'Failed to fetch team name');
      }

      // Fetch user IDs from user_teams table
      const { data: userTeamData, error: userTeamError } = await supabase
        .from('user_teams')
        .select('user_id')
        .eq('team_id', teamId);

      if (userTeamError) {
        throw new Error(userTeamError.message || 'Failed to fetch team members');
      }

      if (!userTeamData || userTeamData.length === 0) {
        toast({
          title: 'Warning',
          description: 'No users found for this team.',
          variant: 'default',
        });
        return;
      }

      const userIds = userTeamData.map(row => row.user_id);

      // Fetch user details from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name, phone_number, email')
        .in('id', userIds);

      if (userError) {
        throw new Error(userError.message || 'Failed to fetch user details');
      }

      // Prepare payload for Zapier as a single object with team name and users array
      const payload = {
        team_name: teamData.name,
        users: userData.map(user => ({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          phone_number: user.phone_number || '',
          email: user.email || ''
        }))
      };


      // Send to Zapier webhook without Content-Type header to avoid CORS issue
      const response = await fetch('https://hooks.zapier.com/hooks/catch/19488085/uuvb1m8/', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send data to Zapier');
      }

      // Update empower_created to true
      const { error: updateError } = await supabase
        .from('teams')
        .update({ empower_created: true })
        .eq('id', teamId);

      if (updateError) {
        throw new Error(updateError.message || 'Failed to update empower_created status');
      }

      await fetchTeams();

      toast({
        title: 'Success',
        description: 'Team data sent to Empower successfully',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to create team with Empower');
      toast({
        title: 'Error',
        description: err.message || 'Failed to create team with Empower',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [toast, fetchTeams]);

  // Fetch teams on mount and when tenant changes
  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.tenant_id]);

  const createTeamWithSES = useCallback(async (teamId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: 'Error',
          description: 'You must be logged in to perform this action.',
          variant: 'destructive',
        });
        throw new Error('No active session');
      }

      // Check if team has already been created with SES
      const { data: teamCheck, error: teamCheckError } = await supabase
        .from('teams')
        .select('ses_created')
        .eq('id', teamId)
        .single();

      if (teamCheckError) {
        throw new Error(teamCheckError.message || 'Failed to check team status');
      }

      if (teamCheck.ses_created) {
        toast({
          title: 'Warning',
          description: 'This team has already been created with SES.',
          variant: 'default',
        });
        return;
      }

      // Fetch team name
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('name')
        .eq('id', teamId)
        .single();

      if (teamError || !teamData) {
        throw new Error(teamError?.message || 'Failed to fetch team name');
      }

      // Fetch user IDs from user_teams table
      const { data: userTeamData, error: userTeamError } = await supabase
        .from('user_teams')
        .select('user_id')
        .eq('team_id', teamId);

      if (userTeamError) {
        throw new Error(userTeamError.message || 'Failed to fetch team members');
      }

      if (!userTeamData || userTeamData.length === 0) {
        toast({
          title: 'Warning',
          description: 'No users found for this team.',
          variant: 'default',
        });
        return;
      }

      const userIds = userTeamData.map(row => row.user_id);

      // Fetch user details from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name, phone_number, email')
        .in('id', userIds);

      if (userError) {
        throw new Error(userError.message || 'Failed to fetch user details');
      }

      // Prepare payload for Zapier as a single object with team name and users array
      const payload = {
        team_name: teamData.name,
        users: userData.map(user => ({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          phone_number: user.phone_number || '',
          email: user.email || ''
        }))
      };


      // Send to Zapier webhook without Content-Type header to avoid CORS issue
      const response = await fetch('https://hooks.zapier.com/hooks/catch/19488085/utai1t9/', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send data to Zapier');
      }

      // Update ses_created to true
      const { error: updateError } = await supabase
        .from('teams')
        .update({ ses_created: true })
        .eq('id', teamId);

      if (updateError) {
        throw new Error(updateError.message || 'Failed to update ses_created status');
      }

      await fetchTeams();

      toast({
        title: 'Success',
        description: 'Team data sent to SES successfully',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to create team with SES');
      toast({
        title: 'Error',
        description: err.message || 'Failed to create team with SES',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [toast, fetchTeams]);

  return {
    teams,
    isLoading,
    error,
    fetchTeams,
    createTeam,
    updateTeam,
    deleteTeam,
    createTeamWithEmpower,
    createTeamWithSES
  };
};